import type { ReactNode } from 'react';
import type { AiJobStatus } from '@chefty/shared-types';

/** A framed surface — the base unit of the console. */
export function Panel({
  children,
  className = '',
  pad = true,
  style,
}: {
  children: ReactNode;
  className?: string;
  pad?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={style}
      className={`rounded-2xl border border-cream-600/15 bg-ink-900/70 shadow-[0_1px_0_0_rgba(255,255,255,0.03)_inset,0_20px_40px_-30px_rgba(0,0,0,0.9)] backdrop-blur-sm ${
        pad ? 'p-5' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
}

/** A small uppercase section eyebrow. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-cream-400">
      {children}
    </span>
  );
}

/** Headline KPI tile with an optional accent and footnote. */
export function StatCard({
  label,
  value,
  sub,
  accent = 'saffron',
  delay = 0,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: 'saffron' | 'teal' | 'cream';
  delay?: number;
}) {
  const bar =
    accent === 'teal' ? 'bg-teal-400' : accent === 'cream' ? 'bg-cream-400' : 'bg-saffron-400';
  return (
    <Panel className="rise relative overflow-hidden" style={{ animationDelay: `${delay}ms` }}>
      <div className={`absolute left-0 top-5 h-8 w-[3px] rounded-r ${bar}`} />
      <div className="pl-3">
        <Eyebrow>{label}</Eyebrow>
        <div className="tnum mt-2 font-display text-3xl font-bold leading-none text-cream-50">
          {value}
        </div>
        {sub != null && <div className="mt-2 text-sm text-cream-400">{sub}</div>}
      </div>
    </Panel>
  );
}

const STATUS_STYLES: Record<AiJobStatus, string> = {
  pending: 'text-cream-200 bg-cream-600/15 border-cream-600/25',
  processing: 'text-info bg-info/10 border-info/30',
  needs_review: 'text-warn bg-warn/10 border-warn/30',
  completed: 'text-ok bg-ok/10 border-ok/30',
  failed: 'text-bad bg-bad/10 border-bad/30',
  cancelled: 'text-cream-400 bg-cream-600/10 border-cream-600/20',
};

const STATUS_LABEL: Record<AiJobStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  needs_review: 'Needs review',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/** Pill for an import-job lifecycle status. */
export function StatusBadge({ status }: { status: AiJobStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {STATUS_LABEL[status]}
    </span>
  );
}

/** A neutral chip — provider names, source types, counts. */
export function Tag({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'saffron' | 'teal' }) {
  const styles =
    tone === 'saffron'
      ? 'text-saffron-300 bg-saffron-500/10 border-saffron-500/25'
      : tone === 'teal'
        ? 'text-teal-400 bg-teal-500/10 border-teal-500/25'
        : 'text-cream-200 bg-cream-600/10 border-cream-600/20';
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-xs ${styles}`}>
      {children}
    </span>
  );
}

/** Saffron spinner. */
export function Spinner({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-block h-5 w-5 animate-spin rounded-full border-2 border-cream-600/30 border-t-saffron-400 ${className}`}
    />
  );
}

/** Centered loading state for a panel. */
export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-cream-400">
      <Spinner />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/** Empty / error state for tables and panels. */
export function EmptyState({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="px-6 py-16 text-center">
      <p className="font-display text-lg text-cream-200">{title}</p>
      {hint != null && <p className="mt-1 text-sm text-cream-400">{hint}</p>}
    </div>
  );
}

/** A live, pulsing indicator dot + label for auto-refreshing surfaces. */
export function LiveBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs text-cream-400">
      <span className="live-dot h-2 w-2 rounded-full bg-ok" />
      {label}
    </span>
  );
}
